import React, { useEffect, useState, useMemo } from "react";
import { Image, Pagination, Empty } from "antd";
import { Col, Row, Container } from "react-bootstrap";
import BreadcrumbLayout from "../../component/Breadcrumb";
import { useTranslation } from "react-i18next";
import Animation from "../../assets/images/Animation.gif";
import { ImageGalleryService } from "../../Services/ImageGalleryService";
import demoUser from "../../assets/images/broken-img.jpg";
import loadingImg from "../../assets/images/loading-img.gif";
import PhotoGallerySidebar from "./PhotoGallerySidebar";

export default function PressGallery() {
  const { t, i18n } = useTranslation();

  const menuData =
    t("menu.media-gallery", {
      returnObjects: true,
    }) || {};

  const imageGalleryService = useMemo(
    () => new ImageGalleryService(),
    []
  );

  const [loadingTblData, setLoadingTblData] = useState(false);
  const [tblData, setTblData] = useState([]);

  const [lang, setLang] = useState(
    localStorage.getItem("language") === "en" ? 2 : 1
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);

  /*
   * Language change
   */
  useEffect(() => {
    setLang(
      localStorage.getItem("language") === "en" ? 2 : 1
    );
  }, [i18n.language]);

  /*
   * 1. Get only gallery metadata/list
   *
   * IMPORTANT:
   * यहां actual images download नहीं हो रही हैं।
   */
  useEffect(() => {
    let isMounted = true;

    const getGalleryData = async () => {
      try {
        setLoadingTblData(true);

        const res =
          await imageGalleryService.GetImageListPublic(
            2,
            lang,
            -1
          );

        if (!isMounted) return;

        const data = (res?.data?.table || []).map(
          (item, index) => ({
            key: index + 1,

            imageId: item.imageId,

            description: item.description,

            imagenotes: item.imagenotes,

            doc: {
              docMimeType: item.docMimeType,
              docUniqueId: item.docUniqueId,
            },

            /*
             * Initially loading image
             */
            ImgUrl: loadingImg,

            /*
             * यह पता रखने के लिए कि image already loaded है
             */
            imageLoaded: false,
          })
        );

        setTblData(data);

        /*
         * अगर language बदलने पर data बदला है
         * तो हमेशा page 1 से शुरू करें
         */
        setCurrentPage(1);
      } catch (error) {
        console.error(
          "Error loading gallery data:",
          error
        );

        if (isMounted) {
          setTblData([]);
        }
      } finally {
        if (isMounted) {
          setLoadingTblData(false);
        }
      }
    };

    getGalleryData();

    return () => {
      isMounted = false;
    };
  }, [imageGalleryService, lang]);

  /*
   * 2. Calculate current page
   */
  const indexOfFirstItem =
    (currentPage - 1) * itemsPerPage;

  const indexOfLastItem =
    indexOfFirstItem + itemsPerPage;

  const currentImages = tblData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  /*
   * 3. केवल current page की images download करें
   */
  useEffect(() => {
    if (currentImages.length === 0) {
      return;
    }

    let cancelled = false;

    const loadCurrentPageImages = async () => {
      /*
       * Current page की images को एक-एक करके load करें
       */
      for (const item of currentImages) {
        /*
         * अगर image पहले से load है
         * तो दोबारा API call नहीं होगी
         */
        if (item.imageLoaded) {
          continue;
        }

        /*
         * Document ID नहीं है
         */
        if (!item.doc?.docUniqueId) {
          setTblData((prevData) =>
            prevData.map((data) =>
              data.imageId === item.imageId
                ? {
                    ...data,
                    ImgUrl: demoUser,
                    imageLoaded: true,
                  }
                : data
            )
          );

          continue;
        }

        try {
          const res =
            await imageGalleryService.ViewDocument(
              item.doc.docUniqueId
            );

          if (cancelled) {
            return;
          }

          if (res.status === 200) {
            const fileBlob = new Blob(
              [res.data],
              {
                type: item.doc.docMimeType,
              }
            );

            const imageUrl =
              URL.createObjectURL(fileBlob);

            /*
             * केवल उसी image को update करें
             */
            setTblData((prevData) =>
              prevData.map((data) =>
                data.imageId === item.imageId
                  ? {
                      ...data,
                      ImgUrl: imageUrl,
                      imageLoaded: true,
                    }
                  : data
              )
            );
          } else {
            throw new Error(
              "Image request failed"
            );
          }
        } catch (error) {
          console.error(
            `Error fetching image ${item.imageId}:`,
            error
          );

          if (!cancelled) {
            setTblData((prevData) =>
              prevData.map((data) =>
                data.imageId === item.imageId
                  ? {
                      ...data,
                      ImgUrl: demoUser,
                      imageLoaded: true,
                    }
                  : data
              )
            );
          }
        }
      }
    };

    loadCurrentPageImages();

    return () => {
      cancelled = true;
    };

    /*
     * IMPORTANT:
     *
     * currentImages को dependency में नहीं रखा गया है।
     *
     * क्योंकि currentImages हर render पर नया array बनता है।
     *
     * इसकी जगह ये 3 values पर्याप्त हैं।
     */
  }, [
    currentPage,
    itemsPerPage,
    tblData.length,
    imageGalleryService,
  ]);

  /*
   * 4. Pagination
   */
  const handlePaginationChange = (
    page,
    pageSize
  ) => {
    /*
     * अगर page size बदला है
     * तो page 1 पर जाना बेहतर है
     */
    if (pageSize !== itemsPerPage) {
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }

    setItemsPerPage(pageSize);

    /*
     * Page के ऊपर scroll करना हो तो
     */
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <BreadcrumbLayout title="Press Gallery" />

      <Container fluid>
        <Row>

          {/* Sidebar */}
          <PhotoGallerySidebar
            menuData={menuData}
          />

          {/* Content */}
          <Col
            md={9}
            className="content order-md-last order-first"
          >
            <Row>

              {/* Heading */}
              <Col
                xs={12}
                className="heading"
              >
                <h3>
                  {t("breadcrumb.press-gallery")}
                </h3>
              </Col>

              <Col
                xs={12}
                className="mb-1 mt-1"
              >

                {/* Main loading animation */}
                {loadingTblData && (
                  <img
                    src={Animation}
                    alt="Loading"
                  />
                )}

                {/* Gallery */}
                {currentImages.length > 0 ? (
                  <>
                    <Row className="galleryWrap">

                      <Image.PreviewGroup>
                        {currentImages.map(
                          (slide) => (
                            <Col
                              lg={4}
                              key={slide.imageId}
                            >
                              <Image
                                src={slide.ImgUrl}
                                alt={
                                  "Image-" +
                                  slide.imageId
                                }

                                /*
                                 * Broken image आने पर
                                 * demo image दिखाएं
                                 */
                                onError={(e) => {
                                  e.currentTarget.src =
                                    demoUser;
                                }}
                              />
                            </Col>
                          )
                        )}
                      </Image.PreviewGroup>

                    </Row>

                    {/* Pagination */}
                    <Row justify="center">
                      <Col>
                        <div
                          className="img-pagination"
                          style={{
                            paddingBottom: "20px",
                          }}
                        >
                          <Pagination
                            current={currentPage}
                            pageSize={itemsPerPage}
                            total={tblData.length}
                            onChange={
                              handlePaginationChange
                            }
                            showSizeChanger={true}
                            pageSizeOptions={[
                              "9",
                              "18",
                              "36",
                              "72",
                            ]}
                          />
                        </div>
                      </Col>
                    </Row>
                  </>
                ) : (
                  /*
                   * No data
                   */
                  <Row>
                    <Col>
                      {!loadingTblData && (
                        <Empty
                          description={
                            lang === 1 ? (
                              <span
                                style={{
                                  fontWeight:
                                    "bold",
                                }}
                              >
                                वर्तमान में, प्रदर्शित
                                करने के लिए कोई डेटा
                                नहीं है ।
                              </span>
                            ) : (
                              <span
                                style={{
                                  fontWeight:
                                    "bold",
                                }}
                              >
                                Currently, There are no
                                data to display.
                              </span>
                            )
                          }
                        />
                      )}
                    </Col>
                  </Row>
                )}

              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
}